import { GridLegacy as Grid, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import { api, User } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { ICriteria } from "./common/types";
import { useUserParams } from "../../../context/user-params-context";

interface PortalUser {
  uuid: string,
  firstName?: string,
  lastName?: string
}

export const ReceptionistSelector: FC<ICriteria> = () => {
  const { t } = useTranslation();
  const { userParams, setUserParams, clearParams } = useUserParams()

  const listReceptionists = api.hooks.chart.listReceptionists();
  const session = useContext(SessionContext);
  const [receptionists, setReceptionists] = useState<PortalUser[]>([]);

  const convertReceptionistsListToUuidList = (receptionists: User[]): string[] => {
    return (receptionists
      .filter((receptionistObject: User) => typeof receptionistObject.uuid === 'string')
      .map((receptionist: User) => receptionist.uuid) as unknown as string[])
  };

  useEffect(() => {
    listReceptionists.send(
      session.current.context,
      {},
      (response: User[] | null) => {
        if (response) {
          const allReceptionists: PortalUser = {
            uuid: convertReceptionistsListToUuidList(response).join(),
            firstName: t('general.all'),
            lastName: ''
          }

          const mappedReceptionists = response.map(receptionist => {
            return {
              uuid: receptionist.uuid,
              firstName: receptionist.firstName,
              lastName: receptionist.lastName
            }
          }).sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });

          setReceptionists([allReceptionists, ...mappedReceptionists]);
          setUserParams({ "@u_receptionistIds": allReceptionists.uuid.split(',') });
        }
      }
    );
    return () => {
      setReceptionists([]);
      clearParams(['@u_receptionistIds']);
    }
  }, []);

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setUserParams({ "@u_receptionistIds": value.split(',') });
  };

  return (
    <Grid item sm={4} md={2}>
      <FormControl sx={{ minWidth: 160 }} size="small" fullWidth>
        <InputLabel>{t("input.criteria.labels.selectReceptionist")}</InputLabel>
        <Select
          id={"receptionist-criteria"}
          value={String(userParams["@u_receptionistIds"]?.join())}
          label={t("input.criteria.labels.selectReceptionist")}
          onChange={handleChange}
        >
          {receptionists.map((receptionist) => (
            <MenuItem key={receptionist.uuid} value={receptionist.uuid}>
              {`${receptionist.firstName} ${receptionist.lastName}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid >
  );
};

export default ReceptionistSelector;
